import { lfm2Prompt, type Generator } from '@manudev/assistant';
import { create } from 'zustand';
import type { WorkerEvent, WorkerRequest } from './model.worker';

// All LFM2.5 instruct builds share the chat format the assistant writes, so any of them
// drops in. Sizes are the weights plus tokenizer as served by the hub, per quantisation;
// the worker picks q4f16 when the GPU has shader-f16 and q4 otherwise.
export const MODELS = [
  {
    id: 'LiquidAI/LFM2.5-350M-ONNX',
    label: 'LFM2.5-350M',
    sizeMB: { q4f16: 258, q4: 297 },
    note: 'fast, any WebGPU laptop',
  },
  {
    id: 'LiquidAI/LFM2.5-1.2B-Instruct-ONNX',
    label: 'LFM2.5-1.2B',
    sizeMB: { q4f16: 727, q4: 812 },
    note: 'better tool choice, needs a real GPU',
  },
  {
    id: 'LiquidAI/LFM2.5-2.6B-ONNX',
    label: 'LFM2.5-2.6B',
    sizeMB: { q4f16: 1537, q4: 1857 },
    note: 'strongest, 4 GB+ of GPU memory',
  },
] as const;
export type ModelChoice = (typeof MODELS)[number];
export type Dtype = keyof ModelChoice['sizeMB'];

export type LocalStatus = 'unsupported' | 'idle' | 'loading' | 'ready' | 'error';

interface Stats {
  tokens: number;
  ms: number;
  firstTokenMs: number;
}

interface LocalModelState {
  status: LocalStatus;
  model: ModelChoice;
  device: 'webgpu' | 'wasm';
  dtype: Dtype;
  f16: boolean;
  gpu: string | null;
  progress: { file: string; loaded: number; total: number } | null;
  loadedTotal: number;
  error: string | null;
  last: Stats | null;
  // text of the generation in flight, token by token
  live: string;
  generations: number;
  probe: { text: string; tokens: number; ms: number } | { error: string } | null;
  choose: (id: string) => void;
  load: () => void;
  probeModel: () => Promise<void>;
}

const params = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
const forcedDevice = params.get('device') === 'wasm' ? 'wasm' : null;
const hasWebGPU = typeof navigator !== 'undefined' && 'gpu' in navigator;
const preset = MODELS.find(
  (m) =>
    m.label.toLowerCase().includes((params.get('model') ?? '').toLowerCase()) &&
    params.get('model'),
);

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<
  number,
  { resolve: (text: string) => void; reject: (err: Error) => void }
>();

export const useLocalModel = create<LocalModelState>((set, get) => ({
  status: hasWebGPU || forcedDevice ? 'idle' : 'unsupported',
  model: preset ?? MODELS[0],
  device: forcedDevice ?? 'webgpu',
  dtype: 'q4f16',
  f16: true,
  gpu: null,
  progress: null,
  loadedTotal: 0,
  error: null,
  last: null,
  live: '',
  generations: 0,
  probe: null,
  choose: (id) => {
    if (get().status === 'loading' || get().status === 'ready') return;
    const model = MODELS.find((m) => m.id === id);
    if (model) set({ model });
  },
  probeModel: async () => {
    const t0 = performance.now();
    try {
      const text = await localGenerator.generate(
        lfm2Prompt(
          [
            { role: 'system', content: 'You are a terse assistant.' },
            { role: 'user', content: 'Reply with exactly: OK, model online.' },
          ],
          [],
        ),
        { maxNewTokens: 24 },
      );
      const last = get().last;
      set({ probe: { text, tokens: last?.tokens ?? 0, ms: performance.now() - t0 } });
    } catch (err) {
      set({ probe: { error: (err as Error)?.message ?? String(err) } });
    }
  },
  load: () => {
    if (get().status !== 'idle' && get().status !== 'error') return;
    set({ status: 'loading', error: null, progress: null, loadedTotal: 0 });
    const files = new Map<string, number>();
    worker = new Worker(new URL('./model.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = ({ data }: MessageEvent<WorkerEvent>) => {
      switch (data.type) {
        case 'progress': {
          files.set(data.file, data.loaded);
          let sum = 0;
          for (const v of files.values()) sum += v;
          set({
            progress: { file: data.file, loaded: data.loaded, total: data.total },
            loadedTotal: sum,
          });
          break;
        }
        case 'plan':
          set({ dtype: data.dtype === 'q4' ? 'q4' : 'q4f16', f16: data.f16, gpu: data.gpu });
          break;
        case 'ready':
          set({ status: 'ready', progress: null, gpu: data.gpu });
          break;
        case 'token':
          set((s) => ({ live: s.live + data.text }));
          break;
        case 'result': {
          pending.get(data.id)?.resolve(data.text);
          pending.delete(data.id);
          set((s) => ({
            last: { tokens: data.tokens, ms: data.ms, firstTokenMs: data.firstTokenMs },
            live: '',
            generations: s.generations + 1,
          }));
          break;
        }
        case 'error': {
          if (data.id != null) {
            pending.get(data.id)?.reject(new Error(data.message));
            pending.delete(data.id);
            set({ live: '' });
          } else set({ status: 'error', error: data.message });
          break;
        }
      }
    };
    worker.onerror = (e) => set({ status: 'error', error: e.message });
    send({ type: 'load', modelId: get().model.id, device: get().device });
  },
}));

function send(req: WorkerRequest) {
  worker?.postMessage(req);
}

export const localGenerator: Generator = {
  generate(prompt, opts) {
    if (useLocalModel.getState().status !== 'ready')
      return Promise.reject(new Error('local model not ready'));
    const id = nextId++;
    useLocalModel.setState({ live: '' });
    return new Promise<string>((resolve, reject) => {
      pending.set(id, { resolve, reject });
      send({ type: 'generate', id, prompt, maxNewTokens: opts?.maxNewTokens ?? 256 });
    });
  },
};
